import CustomTooltip from '../../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Close,
    HelpOutline,
    NavigateBefore,
    NavigateNext,
} from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import {
    FormProvider,
    useFieldArray,
    useForm,
    useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import {
    useAddExpenseAttachmentsMutation,
    useAddExpenseClaimMutation,
    useDeleteExpenseAttachmentMutation,
    useDeleteExpenseClaimMutation,
    useEditExpenseClaimMutation,
    useGetAllReservationsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { CustomTextField } from '../../../../layout/styledcomponents/CustomTextField';
import { theme } from '../../../../shared/theme';
import { handleHttpError } from '../../../../shared/utils';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormDateFilter from '../../../shared/FormDateFilter';
import FormTextField from '../../../shared/FormTextField';
import FormUpload from '../../../shared/FormUpload';
import toast from '../../../shared/Toast';
import { Media } from '../../../types/user';

interface Props {
    reservation?: any;
    open: boolean;
    expenses?: any;
    onClose: () => void;
    onSubmit?: any;
    isEditing?: boolean;
    index?: any;
}

interface ExpensesInterface {
    id?: number;
    name: string;
    description: string;
    amount?: number;
    date: Date | string;
    createdBy?: {
        email?: string;
        firstName?: string;
        lastName?: string;
    };
    bookingId?: number;
    attachments: {
        src?: string;
        name: string;
        uuid: string;
    }[];
    fullyPaidAt?: Date;
    billId?: number;
    invoiceId?: number;
    hostPaidAt?: Date;
    module?: {
        id: number;
        name: string;
        type: string;
    };
    owner?: {
        id: number;
        name: string;
    };
    guest?: {
        id: number;
        name: string;
    };
    isDraft: boolean;
}

const Schema = z.object({
    amount: z.preprocess(
        (v) => (v === '' || v === null || v === undefined ? undefined : v),
        z.coerce
            .number()
            .min(1, { message: 'Required' })
            .max(5000, { message: 'Max limit reached' }),
    ),
    bookingId: z.coerce.number({ message: 'Required' }),
    name: z.coerce
        .string()
        .min(4, { message: 'More than 4 characters required' })
        .max(25, { message: 'Max 25 character limit reached' }),
    description: z.coerce
        .string()
        .min(5, { message: 'More than 5 characters required' })
        .max(1000, { message: 'Character limit reached' }),
    date: z.coerce.date(),
    attachments: z
        .array(
            z.object({
                uuid: z.string(),
                name: z.string(),
                src: z.string().optional(),
            }),
        )
        .min(1, { message: 'At least one attachment is required' }),
    isDraft: z.boolean().default(false),
});

const tooltipByLabel: Record<string, string> = {
    Reservation: 'The reservation this expense is applied to',
    Status: 'If you have received a payment from 7DAY for this expense',
    Guest: 'The guest this expense was for',
    'Expense Name':
        "Specify the name of the expense, such as 'Woolworths Groceries for Dinner'",
    'Expense Date': 'The date the expense was incurred on',
    Amount: 'The total amount of the expense to be billed inc GST',
    Description: 'Description of the expense i.e BBQ meat, Salad and Bread',
    Attachments: 'Receipts/invoices of the expense',
    'Created By': 'Person who added this expense',
    State: `If an expense is in draft it won't be reimbursed. Converting a draft expense to an active expense cannot be undone.`,
};

const getAttachmentErrorMessage = (error: any) => {
    const errorMessage = error?.data?.message || '';
    if (errorMessage.includes('expected size is less than')) {
        return 'One or more of the attachments exceeds the 10MB size limit.';
    }
    if (errorMessage.includes('expected type is')) {
        return 'Invalid format. Expected formats are: image/*|.pdf|.doc|.docx|text/plain|.xlsx|.xls|.csv';
    }
    return 'Failed to add attachments.';
};

const buildReservationOptions = (allReservations: any[] | undefined) =>
    allReservations?.map((res: any) => ({
        value: res.id,
        label: `Reservation #${res.id} - ${res.module?.name} - ${res.guest?.name || res.owner?.name} (${res.status})`,
    })) || [];

const asDateOrEmpty = (value: any) => {
    if (!value) return undefined;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
};

const NavigationControls = ({
    atStart,
    atEnd,
    onPrev,
    onNext,
    showLabels = true,
    compact = false,
}: {
    atStart: boolean;
    atEnd: boolean;
    onPrev: () => void;
    onNext: () => void;
    showLabels?: boolean;
    compact?: boolean;
}) => (
    <ButtonGroup
        size='small'
        variant='outlined'
        sx={
            compact
                ? {
                      height: 34,
                      flexShrink: 0,
                      '& .MuiButton-root': { minWidth: 44, px: 1 },
                  }
                : undefined
        }
    >
        <Button
            startIcon={<NavigateBefore />}
            onClick={onPrev}
            disabled={atStart}
        >
            {showLabels ? 'Prev' : null}
        </Button>
        <Button endIcon={<NavigateNext />} onClick={onNext} disabled={atEnd}>
            {showLabels ? 'Next' : null}
        </Button>
    </ButtonGroup>
);

const ExpenseModal: React.FC<Props> = ({
    reservation,
    expenses,
    open,
    onClose,
    isEditing = false,
    index,
}) => {
    const isMobile = useMediaQuery('(max-width: 700px)');

    const { data: allReservations } = useGetAllReservationsQuery({
        status: ['pending', 'approved', 'completed'],
        bookerType: 'guest',
        sort: 'startDate',
    });

    const [addNewExpenseClaim] = useAddExpenseClaimMutation();
    const [editExpense] = useEditExpenseClaimMutation();
    const [deleteExpense] = useDeleteExpenseClaimMutation();
    const [addAttachments] = useAddExpenseAttachmentsMutation();
    const [deleteAttachment] = useDeleteExpenseAttachmentMutation();
    const fullScreen = useMediaQuery('(max-width: 600px)');
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [cancel, setCancel] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (!isEditing) {
            setCurrentIndex(null);
            return;
        }
        setCurrentIndex(typeof index === 'number' ? index : 0);
    }, [open, isEditing, index]);

    const currentExpense = useMemo(() => {
        if (!isEditing) return null;
        if (currentIndex === null) return null;
        return expenses?.[currentIndex] ?? null;
    }, [expenses, currentIndex, isEditing]);

    const reservationOptions = useMemo(
        () => buildReservationOptions(allReservations),
        [allReservations],
    );

    const isDisabled = Boolean(
        currentExpense?.fullyPaidAt || currentExpense?.billId,
    );

    const defaultValues = useMemo<ExpensesInterface>(() => {
        if (isEditing) {
            return {
                name: currentExpense?.name || '',
                description: currentExpense?.description || '',
                amount:
                    currentExpense?.amount !== undefined &&
                    currentExpense?.amount !== null
                        ? Number(currentExpense.amount)
                        : undefined,
                bookingId: reservation?.id || currentExpense?.bookingId,
                date: asDateOrEmpty(currentExpense?.date) || new Date(),
                attachments: (currentExpense?.attachments || []).map(
                    (a: any) => ({
                        uuid: a.uuid,
                        name: a.name,
                        src: a.src,
                    }),
                ),
                isDraft: Boolean(currentExpense?.isDraft),
                id: currentExpense?.id,
                createdBy: currentExpense?.createdBy,
                module: currentExpense?.module,
                guest: currentExpense?.guest,
                owner: currentExpense?.owner,
                hostPaidAt: currentExpense?.hostPaidAt,
                fullyPaidAt: currentExpense?.fullyPaidAt,
                billId: currentExpense?.billId,
                invoiceId: currentExpense?.invoiceId,
            };
        }

        return {
            name: '',
            description: '',
            amount: undefined,
            bookingId: reservation?.id,
            date: new Date(),
            attachments: [],
            isDraft: false,
        };
    }, [isEditing, currentExpense, reservation]);

    const methods = useForm<ExpensesInterface>({
        defaultValues,
        resolver: zodResolver(Schema),
        mode: 'onChange',
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isSubmitting, isValid },
        clearErrors,
        watch,
        setValue,
        getValues,
    } = methods;

    useEffect(() => {
        if (!open) return;
        reset(defaultValues);
        setCancel(false);
    }, [open, defaultValues, reset]);

    const { remove } = useFieldArray<ExpensesInterface, 'attachments'>({
        control,
        name: 'attachments',
    });

    const attachments = useWatch({ control, name: 'attachments' });

    const handleNextExpense = () => {
        if (!expenses?.length) return;
        if (currentIndex === null) return;
        if (currentIndex >= expenses.length - 1) return;
        setCurrentIndex((v) => (v === null ? 0 : v + 1));
    };

    const handlePreviousExpense = () => {
        if (!expenses?.length) return;
        if (currentIndex === null) return;
        if (currentIndex <= 0) return;
        setCurrentIndex((v) => (v === null ? 0 : v - 1));
    };

    const atStart =
        !expenses?.length || currentIndex === null ? true : currentIndex <= 0;
    const atEnd =
        !expenses?.length || currentIndex === null
            ? true
            : currentIndex >= expenses.length - 1;

    useEffect(() => {
        if (!isEditing) return;
        if (currentIndex === null) return;
        if (!expenses?.length) return;

        const next = expenses[currentIndex];
        reset({
            name: next?.name || '',
            description: next?.description || '',
            amount:
                next?.amount !== undefined && next?.amount !== null
                    ? Number(next.amount)
                    : undefined,
            bookingId: reservation?.id || next?.bookingId,
            date: asDateOrEmpty(next?.date) || new Date(),
            attachments: (next?.attachments || []).map((a: any) => ({
                uuid: a.uuid,
                name: a.name,
                src: a.src,
            })),
            isDraft: Boolean(next?.isDraft),
            id: next?.id,
            createdBy: next?.createdBy,
            module: next?.module,
            guest: next?.guest,
            owner: next?.owner,
            hostPaidAt: next?.hostPaidAt,
            fullyPaidAt: next?.fullyPaidAt,
            billId: next?.billId,
            invoiceId: next?.invoiceId,
        });
        setCancel(false);
    }, [currentIndex, expenses, isEditing, reset, reservation]);

    const handleCancel = () => {
        if (isDirty && !cancel) {
            setCancel(true);
            return;
        }
        if (cancel) reset(defaultValues);
        setCancel(false);
        setCurrentIndex(null);
        onClose();
    };

    const handleAddAttachments = async (
        files: File[],
    ): Promise<Media[] | undefined> => {
        try {
            const formData = new FormData();
            for (const file of files) formData.append('attachments', file);
            const response = await addAttachments(formData).unwrap();
            clearErrors('attachments');
            return response;
        } catch (error: any) {
            toast.error(getAttachmentErrorMessage(error));
            return undefined;
        }
    };

    const handleDeleteAttachment = async (
        uuid: string,
        attachmentIndex: number,
    ) => {
        remove(attachmentIndex);
        try {
            const res: any = await deleteAttachment(uuid);
            if (res?.error) {
                toast.error(handleHttpError(res?.error));
                return;
            }
            toast.success('Attachment removed');
        } catch {
            toast.error('Failed to remove attachment');
        }
    };

    const handleNewExpense = async (data: any) => {
        try {
            const payload = {
                ...data,
                date: moment(data.date).format('YYYY-MM-DD'),
            };
            const res: any = await addNewExpenseClaim(payload).unwrap();
            if (res?.error) {
                toast.error(handleHttpError(res?.error));
                return;
            }
            toast.success('Successfully added expense');
            onClose();
        } catch {
            toast.error('Failed to add expense. Please try again.');
        }
    };

    const handleEditExpense = async (data: any) => {
        try {
            const payload = {
                ...data,
                date: moment(data.date).format('YYYY-MM-DD'),
            };
            const reqInfo = [currentExpense?.id, payload];
            const res: any = await editExpense(reqInfo);
            if (res?.error) {
                toast.error(handleHttpError(res?.error));
                return;
            }
            toast.success('Expense has been edited');
            onClose();
        } catch {
            toast.error('Failed to edit expense');
        }
    };

    const handleDeleteExpense = async () => {
        if (!currentExpense?.id) return;
        try {
            const res: any = await deleteExpense(currentExpense.id).unwrap();
            if (res?.error) {
                toast.error(handleHttpError(res?.error));
                return;
            }
            toast.success('Successfully deleted expense');
            onClose();
        } catch {
            toast.error('Failed to deleted. Please try again.');
        }
    };

    const guestName = currentExpense?.guest?.name || reservation?.guest?.name;

    const statusValue = currentExpense?.hostPaidAt
        ? `Paid - ${moment(currentExpense.hostPaidAt).format('DD/MM/YYYY')}`
        : 'Unpaid';

    const showStateField = Boolean(
        watch('isDraft') !== undefined && currentExpense?.isDraft,
    );

    const onNestedSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        event.stopPropagation();
        setCancel(false);
        if (isEditing && currentExpense)
            return handleSubmit(handleEditExpense)(event);
        return handleSubmit(handleNewExpense)(event);
    };

    const Label = ({
        text,
        required,
    }: {
        text: string;
        required?: boolean;
    }) => (
        <InputLabel
            required={required}
            sx={{
                fontSize: '12px',
                lineHeight: 1.2,
                fontWeight: 700,
            }}
        >
            {text}
            <CustomTooltip
                title={
                    <Typography variant='body3'>
                        <List>
                            <ListItem>{tooltipByLabel[text] || ''}</ListItem>
                        </List>
                    </Typography>
                }
                placement='right'
            >
                <HelpOutline sx={{ fontSize: '16px' }} />
            </CustomTooltip>
        </InputLabel>
    );

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            fullScreen={fullScreen}
            maxWidth='lg'
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : 3,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    display: 'flex',
                    flexDirection: 'column',
                    height: fullScreen ? '100dvh' : undefined,
                    maxHeight: fullScreen ? '100dvh' : 'calc(100dvh - 32px)',
                },
            }}
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={onNestedSubmit}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: fullScreen ? '100%' : 'auto',
                        minHeight: 0,
                    }}
                >
                    <DialogTitle
                        sx={{ flexShrink: 0, alignItems: 'flex-start' }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                minWidth: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <ReceiptLongIcon
                                    sx={{ opacity: 0.55 }}
                                    fontSize='small'
                                />
                                {isEditing
                                    ? `Expense #${currentExpense?.id}${currentExpense?.isDraft ? ' (Draft)' : ''}`
                                    : 'New Expense'}
                            </Box>
                            <Typography
                                variant='body3'
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: 12,
                                }}
                            >
                                {isEditing ? (
                                    currentExpense?.fullyPaidAt ||
                                    currentExpense?.billId ? (
                                        'You cannot edit/delete an expense that has been billed.'
                                    ) : (
                                        <Typography sx={{ fontSize: 10 }}>
                                            Created by:{' '}
                                            {currentExpense?.createdBy
                                                ? `${currentExpense?.createdBy?.firstName ?? ''} ${currentExpense?.createdBy?.lastName ?? ''}`.trim()
                                                : 'Automatically created by system'}
                                        </Typography>
                                    )
                                ) : (
                                    'Once you apply an expense, it will be billed in the next monthly invoice.'
                                )}
                            </Typography>
                            <Collapse in={cancel}>
                                <Alert severity='warning' sx={{ mt: 1 }}>
                                    You have unsaved changes. Are you sure you
                                    want to cancel?
                                </Alert>
                            </Collapse>
                            {isMobile &&
                                isEditing &&
                                (expenses?.length || 0) > 1 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            gap: 1,
                                            mt: 1,
                                        }}
                                    >
                                        <NavigationControls
                                            atStart={atStart}
                                            atEnd={atEnd}
                                            onPrev={handlePreviousExpense}
                                            onNext={handleNextExpense}
                                            showLabels={false}
                                            compact
                                        />
                                    </Box>
                                )}
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {!isMobile &&
                                isEditing &&
                                (expenses?.length || 0) > 1 && (
                                    <NavigationControls
                                        atStart={atStart}
                                        atEnd={atEnd}
                                        onPrev={handlePreviousExpense}
                                        onNext={handleNextExpense}
                                    />
                                )}
                            <IconButton
                                onClick={handleCancel}
                                sx={{ color: 'text.primary' }}
                                aria-label='close'
                            >
                                <Close />
                            </IconButton>
                        </Box>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <Grid container spacing={1}>
                            {!reservation?.id && (
                                <Grid item xs={12} md={6}>
                                    <Label text='Reservation' required />
                                    <FormAutocomplete
                                        name='bookingId'
                                        control={control}
                                        options={reservationOptions}
                                        placeholder='Select experience'
                                        disabled={isDisabled}
                                    />
                                </Grid>
                            )}

                            {reservation?.id && (
                                <Grid item xs={12} md={6}>
                                    <Label text='Reservation' />
                                    <CustomTextField
                                        disabled
                                        value={`Reservation #${reservation?.id} - ${reservation?.module?.name}`}
                                        fullWidth
                                        color='dark'
                                        inputProps={{
                                            style: {
                                                height: '10px',
                                                width: '100%',
                                                backgroundColor: '#f6f8f9',
                                            },
                                        }}
                                    />
                                </Grid>
                            )}

                            {isEditing && (
                                <Grid item xs={12} md={6}>
                                    <Label text='Status' />
                                    <CustomTextField
                                        disabled
                                        value={statusValue}
                                        fullWidth
                                        color='dark'
                                        inputProps={{
                                            style: {
                                                height: '10px',
                                                width: '100%',
                                                backgroundColor: '#f6f8f9',
                                            },
                                        }}
                                    />
                                </Grid>
                            )}

                            {(reservation?.id || isEditing) && (
                                <Grid item xs={12} md={6}>
                                    <Label text='Guest' />
                                    <CustomTextField
                                        disabled
                                        value={guestName || ''}
                                        fullWidth
                                        color='dark'
                                        inputProps={{
                                            style: {
                                                height: '10px',
                                                width: '100%',
                                                backgroundColor: '#f6f8f9',
                                            },
                                        }}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} md={6}>
                                <Label text='Expense Name' required />
                                <FormTextField
                                    name='name'
                                    control={control}
                                    type='string'
                                    disabled={isDisabled}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Label text='Expense Date' required />
                                <FormDateFilter
                                    name='date'
                                    control={control}
                                    disabled={isDisabled}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Label text='Amount' required />
                                <FormTextField
                                    name='amount'
                                    control={control}
                                    type='number'
                                    disabled={isDisabled}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <Typography>$</Typography>
                                            </InputAdornment>
                                        ),
                                        inputProps: {
                                            inputMode: 'decimal',
                                        },
                                        onFocus: () => {
                                            const v = getValues('amount');
                                            if (v === 0)
                                                setValue('amount', undefined, {
                                                    shouldDirty: true,
                                                });
                                        },
                                        onClick: () => {
                                            const v = getValues('amount');
                                            if (v === 0)
                                                setValue('amount', undefined, {
                                                    shouldDirty: true,
                                                });
                                        },
                                    }}
                                />
                            </Grid>

                            {watch('isDraft') !== undefined &&
                                currentExpense?.isDraft && (
                                    <Grid item xs={12} md={6}>
                                        <Label text='State' />
                                        <FormAutocomplete
                                            name='isDraft'
                                            control={control}
                                            options={[
                                                { value: true, label: 'Draft' },
                                                {
                                                    value: false,
                                                    label: 'Active',
                                                },
                                            ]}
                                            placeholder='Select status'
                                            disabled={
                                                isDisabled || !showStateField
                                            }
                                        />
                                    </Grid>
                                )}

                            <Grid item xs={12}>
                                <Label text='Description' required />
                                <FormTextField
                                    name='description'
                                    control={control}
                                    type='string'
                                    multiline
                                    rows={4}
                                    disabled={isDisabled}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Label text='Attachments' required />
                                <FormUpload
                                    name='attachments'
                                    control={control}
                                    onUpload={handleAddAttachments}
                                    showFiles={false}
                                    disabled={isDisabled}
                                    validation={{
                                        acceptedTypes: {
                                            'image/*': [
                                                '.png',
                                                '.jpg',
                                                '.jpeg',
                                                '.gif',
                                                '.webp',
                                            ],
                                            'application/pdf': ['.pdf'],
                                            'application/msword': ['.doc'],
                                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                                ['.docx'],
                                            'text/plain': ['.txt'],
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                                                ['.xlsx'],
                                            'application/vnd.ms-excel': [
                                                '.xls',
                                            ],
                                            'text/csv': ['.csv'],
                                        },
                                        maxFileSize: 10 * 1024 * 1024,
                                        orientation: 'any',
                                    }}
                                    uploadText='Click to browse attachments or drag and drop'
                                />
                            </Grid>

                            {attachments?.length ? (
                                <Grid item xs={12}>
                                    <Box sx={{ mt: 0.5 }}>
                                        {attachments.map((attachment, idx) => (
                                            <Box
                                                key={attachment.uuid || idx}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between',
                                                    gap: 1,
                                                    py: 0.75,
                                                    px: 1,
                                                    borderRadius: 1.5,
                                                    backgroundColor: alpha(
                                                        theme.palette.yellow
                                                            .main,
                                                        0.18,
                                                    ),
                                                    border: `1px solid ${alpha(theme.palette.yellow.main, 0.45)}`,
                                                    mb: 0.75,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '12px',
                                                        cursor: attachment?.src
                                                            ? 'pointer'
                                                            : 'default',
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    onClick={() =>
                                                        attachment?.src
                                                            ? window.open(
                                                                  attachment.src,
                                                                  '_blank',
                                                              )
                                                            : undefined
                                                    }
                                                >
                                                    {attachment.name}
                                                </Typography>

                                                <CustomIconButton
                                                    onClick={() =>
                                                        handleDeleteAttachment(
                                                            attachment.uuid,
                                                            idx,
                                                        )
                                                    }
                                                    disabled={isDisabled}
                                                    sx={{
                                                        '&:hover': {
                                                            cursor: 'pointer',
                                                            color: 'red',
                                                            backgroundColor:
                                                                'transparent',
                                                        },
                                                    }}
                                                >
                                                    <ClearIcon
                                                        sx={{ fontSize: 20 }}
                                                    />
                                                </CustomIconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                            ) : null}
                        </Grid>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            flexShrink: 0,
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 1,
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 1.25,
                                alignItems: isMobile ? 'stretch' : 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            {isEditing ? (
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={handleDeleteExpense}
                                    disabled={isDisabled}
                                    sx={{
                                        width: isMobile ? '100%' : 'auto',
                                        alignSelf: isMobile
                                            ? 'stretch'
                                            : 'center',
                                    }}
                                >
                                    Delete
                                </Button>
                            ) : (
                                <Box />
                            )}

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    width: isMobile ? '100%' : 'auto',
                                }}
                            >
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={handleCancel}
                                    sx={{ flex: isMobile ? 1 : undefined }}
                                >
                                    Close
                                </Button>

                                <Button
                                    variant='contained'
                                    color='primary'
                                    type='submit'
                                    disabled={
                                        isDisabled || isSubmitting || !isValid
                                    }
                                    sx={{ flex: isMobile ? 1 : undefined }}
                                >
                                    Save
                                </Button>
                            </Box>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default ExpenseModal;
